/**
 * Store API Integration
 * Handles fetching store locations from external API with fallback to local data
 */

const STATE_CODE_TO_PROVINCE = {
  // Spain — ISO 3166-2:ES codes (without "ES-" prefix)
  'C': 'A Coruña',
  'AB': 'Albacete',
  'A': 'Alicante',
  'AL': 'Almería',
  'VI': 'Álava',
  'O': 'Asturias',
  'AV': 'Ávila',
  'BA': 'Badajoz',
  'IB': 'Balears',
  'B': 'Barcelona',
  'BU': 'Burgos',
  'CC': 'Cáceres',
  'CA': 'Cádiz',
  'S': 'Cantabria',
  'CS': 'Castellón',
  'CR': 'Ciudad Real',
  'CO': 'Córdoba',
  'CU': 'Cuenca',
  'GI': 'Girona',
  'GR': 'Granada',
  'GU': 'Guadalajara',
  'SS': 'Guipúzcoa',
  'H': 'Huelva',
  'HU': 'Huesca',
  'J': 'Jaén',
  'LE': 'León',
  'L': 'Lleida',
  'LO': 'La Rioja',
  'LU': 'Lugo',
  'M': 'Madrid',
  'MA': 'Málaga',
  'MU': 'Murcia',
  'NA': 'Navarra',
  'OR': 'Ourense',
  'P': 'Palencia',
  'PO': 'Pontevedra',
  'SA': 'Salamanca',
  'TF': 'Santa Cruz de Tenerife',
  'SG': 'Segovia',
  'SE': 'Sevilla',
  'SO': 'Soria',
  'T': 'Tarragona',
  'TE': 'Teruel',
  'TO': 'Toledo',
  'V': 'Valencia',
  'VA': 'Valladolid',
  'BI': 'Vizcaya',
  'ZA': 'Zamora',
  'Z': 'Zaragoza',
  'GC': 'Las Palmas',
  // Portugal — ISO 3166-2:PT codes
  'PT-01': 'Aveiro',
  'PT-02': 'Beja',
  'PT-03': 'Braga',
  'PT-04': 'Bragança',
  'PT-05': 'Castelo Branco',
  'PT-06': 'Coimbra',
  'PT-07': 'Évora',
  'PT-08': 'Faro',
  'PT-09': 'Guarda',
  'PT-10': 'Leiria',
  'PT-11': 'Lisboa',
  'PT-12': 'Portalegre',
  'PT-13': 'Porto',
  'PT-14': 'Santarém',
  'PT-15': 'Setúbal',
  'PT-16': 'Viana do Castelo',
  'PT-17': 'Vila Real',
  'PT-18': 'Viseu',
  'PT-20': 'Açores',
  'PT-30': 'Madeira',
};

/**
 * Validates if a store has valid coordinates
 * @param {Object} apiStore - Store object from API
 * @returns {boolean} - True if store has valid lat/lng
 */
function hasValidCoordinates(apiStore) {
  const lat = apiStore.latitude;
  const lng = apiStore.longitude;

  const isValid = lat !== null &&
    lat !== undefined &&
    lng !== null &&
    lng !== undefined &&
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !isNaN(lat) &&
    !isNaN(lng);

  if (!isValid) {
    console.warn('[Store Locator] Store with invalid coordinates detected:', {
      id: apiStore.id || apiStore.shopify_legacy_id,
      name: apiStore.name,
      latitude: lat,
      longitude: lng,
      address: `${apiStore.address1}, ${apiStore.city}, ${apiStore.country}`
    });
  }

  return isValid;
}

/**
 * Transforms API store data to the format expected by map-location.js
 * @param {Object} apiStore - Store object from API
 * @returns {Object} - Store object in local format
 */
function transformApiStoreToLocal(apiStore) {
  return {
    id: apiStore.shopify_id || `gid://shopify/Location/${apiStore.shopify_legacy_id}`,
    name: apiStore.name,
    address: {
      latitude: apiStore.latitude,
      longitude: apiStore.longitude,
      phone: apiStore.phone || '',
      province: STATE_CODE_TO_PROVINCE[apiStore.id_state] || apiStore.id_state || null,
      address1: apiStore.address1,
      address2: apiStore.address2 || '',
      city: apiStore.city,
      country: apiStore.country,
      zip: apiStore.postcode,
      countryCode: apiStore.id_country,
      hours: JSON.stringify(apiStore.hours || [])
    }
  };
}

/**
 * Fetches store locations from API
 * @param {string} apiUrl - API endpoint URL
 * @returns {Promise<Array|null>} - Array of stores in local format or null if failed
 */
async function fetchStoresFromApi(apiUrl) {
  if (!apiUrl || apiUrl.trim() === '') {
    console.warn('[Store Locator] No API URL configured');
    return null;
  }

  try {
    console.log('[Store Locator] Fetching stores from API:', apiUrl);

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${window.config?.hydrogenBearer}`
      }
    });

    if (!response.ok) {
      console.error(`[Store Locator] API request failed with status ${response.status}: ${response.statusText}`);
      return null;
    }

    const data = await response.json();

    // Handle response structure - API returns { statusCode, data }
    const stores = data.data || data;

    if (!Array.isArray(stores)) {
      console.error('[Store Locator] API response does not contain a valid stores array');
      return null;
    }

    // Filter active stores with valid coordinates and transform to local format
    const validStores = stores.filter(store => {
      if (store.active === false) {
        return false;
      }
      return hasValidCoordinates(store);
    });

    const transformedStores = validStores.map(transformApiStoreToLocal);

    const filteredCount = stores.length - validStores.length;
    if (filteredCount > 0) {
      console.warn(`[Store Locator] Filtered out ${filteredCount} stores with invalid or missing coordinates`);
    }

    console.log(`[Store Locator] Successfully fetched ${transformedStores.length} valid stores from API (${stores.length} total in response)`);
    return transformedStores;

  } catch (error) {
    console.error('[Store Locator] Error fetching stores from API:', error);
    return null;
  }
}

/**
 * Gets store locations either from API or local fallback
 * @returns {Promise<Array>} - Array of stores
 */
async function getStoreLocations() {
  // Get configuration from window.config (set in theme.liquid)
  const useApi = window.config?.storeLocatorUseApi === true;
  const params = new URLSearchParams({
    'shop-id': window.config?.marketId,
    'country-id': window.config?.countryId,
    lang: window.config?.lang
  });
  const apiUrl = `${window.config?.hydrogenEndpoint}/rest/1/stores/search/0.0/0.0/0?${params}`;

  // If API is not enabled, use local data
  if (!useApi) {
    console.log('[Store Locator] Using local store data (API disabled)');
    return window.storeLocations || [];
  }

  // Try to fetch from API
  const apiStores = await fetchStoresFromApi(apiUrl);

  // If API fetch succeeded, use API data
  if (apiStores && apiStores.length > 0) {
    return apiStores;
  }

  // Fallback to local data
  console.log('[Store Locator] Falling back to local store data');
  return window.storeLocations || [];
}

// Export functions for use in other scripts
window.storeApi = {
  getStoreLocations,
  fetchStoresFromApi,
  transformApiStoreToLocal
};
